import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const KONDISI_OPTIONS = ["BAIK", "RUSAK RINGAN", "RUSAK BERAT"];
const empty = { nama: "", luas_indoor: "", luas_outdoor: "", kondisi: "BAIK", jumlah_gedung: "", jumlah_rumah_dinas: "", link_maps: "" };

export default function KawasanFormDialog({ open, onOpenChange, kawasan, onSave }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(kawasan ? {
        nama: kawasan.nama || "",
        luas_indoor: kawasan.luas_indoor || "",
        luas_outdoor: kawasan.luas_outdoor || "",
        kondisi: kawasan.kondisi || "BAIK",
        jumlah_gedung: kawasan.jumlah_gedung_manual ?? kawasan.jumlah_gedung ?? "",
        jumlah_rumah_dinas: kawasan.jumlah_rumah_dinas_manual ?? kawasan.jumlah_rumah_dinas ?? "",
        link_maps: kawasan.link_maps || "",
      } : empty);
    }
  }, [open, kawasan]);

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        nama: form.nama.trim(),
        luas_indoor: Number(form.luas_indoor) || 0,
        luas_outdoor: Number(form.luas_outdoor) || 0,
        kondisi: form.kondisi,
        jumlah_gedung: parseInt(form.jumlah_gedung, 10) || 0,
        jumlah_rumah_dinas: parseInt(form.jumlah_rumah_dinas, 10) || 0,
        link_maps: form.link_maps.trim(),
      });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Gagal menyimpan kawasan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="kawasan-form-dialog">
        <DialogHeader>
          <DialogTitle>{kawasan ? "Ubah Kawasan" : "Tambah Kawasan"}</DialogTitle>
          <DialogDescription>Luas dalam meter persegi (m²). Gedung & rumah dinas dicatat di detail kawasan.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="kw-nama">Nama Kawasan *</Label>
            <Input id="kw-nama" required value={form.nama} onChange={setField("nama")}
              placeholder="Kawasan Kantor Pusat" data-testid="kawasan-form-nama" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="kw-indoor">Luas Indoor (m²)</Label>
              <Input id="kw-indoor" type="number" min="0" step="any" value={form.luas_indoor}
                onChange={setField("luas_indoor")} placeholder="0" data-testid="kawasan-form-indoor" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="kw-outdoor">Luas Outdoor (m²)</Label>
              <Input id="kw-outdoor" type="number" min="0" step="any" value={form.luas_outdoor}
                onChange={setField("luas_outdoor")} placeholder="0" data-testid="kawasan-form-outdoor" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="kw-gedung">Jumlah Gedung</Label>
              <Input id="kw-gedung" type="number" min="0" step="1" value={form.jumlah_gedung}
                onChange={setField("jumlah_gedung")} placeholder="0" data-testid="kawasan-form-gedung" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="kw-rumdin">Jumlah Rumah Dinas</Label>
              <Input id="kw-rumdin" type="number" min="0" step="1" value={form.jumlah_rumah_dinas}
                onChange={setField("jumlah_rumah_dinas")} placeholder="0" data-testid="kawasan-form-rumah-dinas" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="kw-maps">Link Google Maps</Label>
            <Input id="kw-maps" value={form.link_maps} onChange={setField("link_maps")}
              placeholder="https://maps.google.com/..." data-testid="kawasan-form-maps" />
          </div>
          <div className="space-y-1.5">
            <Label>Kondisi</Label>
            <Select value={form.kondisi} onValueChange={(v) => setForm((f) => ({ ...f, kondisi: v }))}>
              <SelectTrigger data-testid="kawasan-form-kondisi"><SelectValue /></SelectTrigger>
              <SelectContent>
                {KONDISI_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
              data-testid="cancel-kawasan-form">Batal</Button>
            <Button type="submit" disabled={saving} data-testid="submit-kawasan-form"
              className="gap-2 bg-blue-600 hover:bg-blue-700 text-white">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Simpan
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
